import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useSelectFlow } from '@/app/guide/guideEditBase';
import { editFlowOpenState, editingFlowState } from '@/state';
import logger from '@/util/logger';
import { AppFlowDao } from '@rock/db-admin';
import type { AppFlowObject, AppFlowParams } from '@rock/types';
import { useAtom } from 'jotai';
import { type FC, useState } from 'react';

const log = logger('FlowDetailsForm');
const appFlowDao = new AppFlowDao();
const nextFlowId = () => `${Date.now()}`;

const FlowDetailsForm: FC<{ params: AppFlowParams }> = ({ params }) => {
    const spaceId = params.spaceId;
    const selectFlow = useSelectFlow(params);
    const [editingFlow, setEditingFlow] = useAtom(editingFlowState);
    const [editFlowOpen, setEditFlowOpen] = useAtom(editFlowOpenState);
    const [error, setError] = useState('');

    const editFlowId = editingFlow?.id;
    const guideName = editingFlow?.name || '';
    const equipmentTypeId = editingFlow?.equipmentTypeId || params.equipmentTypeId;

    const handleSaveFlow = async () => {
        if (!guideName) {
            setError('Please enter a name and select a space.');
            return;
        }

        const flowIdToUpdate = editFlowId || nextFlowId();
        const flowData: AppFlowObject = {
            object: 'app-flow',
            schema: 1,
            space: spaceId,
            equipmentTypeId,
            nodes: [],
            edges: [],
            nextId: 0,
            ...editingFlow,
            id: flowIdToUpdate,
            name: guideName,
        };
        await appFlowDao.setFlow(spaceId, flowIdToUpdate, flowData);

        log.info(editFlowId ? 'Updated guide' : 'Created new guide', {
            id: flowIdToUpdate,
            name: guideName,
            spaceId: spaceId,
            typeId: equipmentTypeId,
        });

        setEditFlowOpen(false);
        setEditingFlow(undefined);
        setError('');

        // A brand-new guide is what you wanted to work on, so open it — which in the editor means
        // navigating to it, and from the Guides table just means selecting it behind the list.
        if (!editFlowId) {
            selectFlow(flowIdToUpdate);
        }
    };

    return (
        <Dialog open={editFlowOpen} onOpenChange={setEditFlowOpen}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{editFlowId ? 'Edit Guide' : 'Create New Guide'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <Label htmlFor="guide-name">Name</Label>
                        <Input
                            id="guide-name"
                            placeholder="Guide Name"
                            value={guideName}
                            onChange={(e) => {
                                if (editingFlow) {
                                    setEditingFlow({ ...editingFlow, name: e.target.value });
                                }
                            }}
                        />
                    </div>
                    {error && <div className="text-red-500 text-sm">{error}</div>}
                </div>
                <DialogFooter>
                    <Button variant="secondary" onClick={() => setEditFlowOpen(false)}>
                        Cancel
                    </Button>
                    <Button onClick={handleSaveFlow}>{editFlowId ? 'Update Guide' : 'Create Guide'}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default FlowDetailsForm;
